import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  AttioMappingAction,
  AttioMappingProposal,
  DiscoveredObjectType,
} from './api';
import {
  type AttioImportWizardState,
  DEFAULT_SELECTED_TYPES,
} from './attioWizardHelpers';

/**
 * Owns the wizard's raw state, the `stateRef`/`controllerRef` render-stable
 * mirrors, and the small synchronous handlers (field changes, reset,
 * dirty-check, close). The three step onBeforeNext/onConfirm handlers (which
 * need this state plus network calls) live in `useAttioWizardStepHandlers`.
 */
export function useAttioWizardState(onClose: () => void) {
  const [apiKey, setApiKey] = useState('');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    () => DEFAULT_SELECTED_TYPES
  );
  const [discoveredTypes, setDiscoveredTypes] = useState<
    DiscoveredObjectType[]
  >([]);
  const [standardAvailability, setStandardAvailability] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [proposalsByEntity, setProposalsByEntity] = useState<Record<
    string,
    AttioMappingProposal[]
  > | null>(null);
  const [proposalsLoadError, setProposalsLoadError] = useState(false);
  const [truncatedByEntity, setTruncatedByEntity] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [totalRecordsByEntity, setTotalRecordsByEntity] = useState<Record<
    string,
    number
  > | null>(null);
  const [actionByKey, setActionByKey] = useState<
    Record<string, AttioMappingAction>
  >({});

  // Keep a ref current during each render so render delegates / canAdvance /
  // onBeforeNext closures read the latest state without capturing it in
  // their dep arrays — prevents the steps useMemo from recomputing on every
  // keystroke.
  const stateRef = useRef<AttioImportWizardState>({
    apiKey,
    connectError,
    selectedTypes,
    discoveredTypes,
    standardAvailability,
    proposalsByEntity,
    proposalsLoadError,
    truncatedByEntity,
    totalRecordsByEntity,
    actionByKey,
  });
  stateRef.current = {
    apiKey,
    connectError,
    selectedTypes,
    discoveredTypes,
    standardAvailability,
    proposalsByEntity,
    proposalsLoadError,
    truncatedByEntity,
    totalRecordsByEntity,
    actionByKey,
  };

  // Tracks the active AbortController so the cleanup effect can abort on
  // unmount, stopping any in-progress polling loop.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      // Intentionally reads the LATEST controllerRef.current at unmount time
      // (not a snapshot from mount) so an in-flight poll started after mount
      // still gets aborted. react-hooks/exhaustive-deps' ref-cleanup warning
      // assumes a DOM-node ref (want the mount-time snapshot); that assumption
      // doesn't hold for this mutable AbortController ref, which is written
      // from the step handlers hook this state is passed into.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      controllerRef.current?.abort();
    };
  }, []);

  const handleApiKeyChange = useCallback((value: string) => {
    setApiKey(value);
    setConnectError(null);
  }, []);

  const handleTypesChange = useCallback((names: string[]) => {
    setSelectedTypes(names);
  }, []);

  const handleActionChange = useCallback(
    (key: string, action: AttioMappingAction) => {
      setActionByKey(prev => ({ ...prev, [key]: action }));
    },
    []
  );

  const reset = useCallback(() => {
    setApiKey('');
    setConnectError(null);
    setSelectedTypes(DEFAULT_SELECTED_TYPES);
    setDiscoveredTypes([]);
    setStandardAvailability(null);
    setProposalsByEntity(null);
    setProposalsLoadError(false);
    setTruncatedByEntity(null);
    setTotalRecordsByEntity(null);
    setActionByKey({});
  }, []);

  // The wizard has unsaved input once a key is typed or the object-type
  // selection diverges from the default (all types). Drives the base Wizard's
  // dirty-close confirmation so in-progress input isn't silently discarded.
  const isDirty = useMemo(() => {
    const selectionChanged =
      selectedTypes.length !== DEFAULT_SELECTED_TYPES.length ||
      DEFAULT_SELECTED_TYPES.some(name => !selectedTypes.includes(name));
    return apiKey.trim() !== '' || selectionChanged;
  }, [apiKey, selectedTypes]);

  // Only place state is reset — clears the pasted key on close.
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  // Abort the in-flight poll and leave the wizard. During a poll every footer
  // CTA and the header close button are disabled (the wizard holds
  // isSubmitting), so this is the user's only exit. Aborting unwinds the
  // awaited poll — it throws AbortError inside onBeforeNext/onConfirm — and
  // closing here rather than waiting on that unwind means one click leaves
  // immediately. handleClose resets state; the abort-aware catch in the step
  // handlers skips its load-error notice so nothing stale survives the reset.
  const handleCancelPoll = useCallback(() => {
    controllerRef.current?.abort();
    handleClose();
  }, [handleClose]);

  return {
    apiKey,
    connectError,
    selectedTypes,
    discoveredTypes,
    standardAvailability,
    proposalsByEntity,
    proposalsLoadError,
    truncatedByEntity,
    totalRecordsByEntity,
    actionByKey,
    setConnectError,
    setSelectedTypes,
    setDiscoveredTypes,
    setStandardAvailability,
    setProposalsByEntity,
    setProposalsLoadError,
    setTruncatedByEntity,
    setTotalRecordsByEntity,
    setActionByKey,
    stateRef,
    controllerRef,
    handleApiKeyChange,
    handleTypesChange,
    handleActionChange,
    isDirty,
    handleClose,
    handleOpenChange,
    handleCancelPoll,
  };
}

export type AttioWizardStateController = ReturnType<typeof useAttioWizardState>;
